import React from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import JSZip from "jszip";
import ReactDOM from "react-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { Provider } from "react-redux";
import { setupStore } from "../../_globalStore/redux/store";
import ThemeProvider from "../../_globalStore/themeProvider/ThemeProvider";
import { DocumentFileRef } from "../../models/documents/document-files";
import { getDownloadedFile } from "../../services/documents";
import { getFileExtension } from "../files-utils/get-file-extension";

export const ROW_CLASSNAME = "rowClassName";
export const HEADER_FOR_PDF = "headerForPDF";
export const CONTENT_GAP = 20;
export const GAPS_BETWEEN_ROWS = 20;

const PAGE_MARGIN = 10; // Padding to every pdf page

export const exportExpenseReportsToPDF = async (
  contentToExport: React.JSX.Element,
  notifyIfErrorOccured: () => void,
  customNameToSave: string
) => {
  const pageHeigth = 1400;
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.transform = "translate(-9999px, -9999px)";
  container.style.width = "1000px";

  const contentWrappedByTheme = (
    <Provider store={setupStore()}>
      <ThemeProvider>
        <CssBaseline>{contentToExport}</CssBaseline>
      </ThemeProvider>
    </Provider>
  );

  try {
    // Render the JSX.Element into the container
    ReactDOM.render(contentWrappedByTheme, container);
    if (container) {
      // Hide specific elements in the clone

      document.body.appendChild(container);

      // block to add margins for rows
      let headerHeight = 0;
      const header = container.querySelectorAll(`div.${HEADER_FOR_PDF}`);
      for (let i = 0; i < header.length; i++) {
        const currHeader = header[i] as HTMLDivElement;
        headerHeight += currHeader.offsetHeight;
      }

      let rowsHeightPerPage = CONTENT_GAP + headerHeight;
      const rows = container.querySelectorAll(`div.${ROW_CLASSNAME}`);

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as HTMLDivElement;
        const currentRowHeight = row.offsetHeight;
        row.style.height = `${currentRowHeight}px`;

        if (rowsHeightPerPage + currentRowHeight <= pageHeigth) {
          if (rowsHeightPerPage + currentRowHeight + GAPS_BETWEEN_ROWS <= pageHeigth) {
            rowsHeightPerPage += currentRowHeight + GAPS_BETWEEN_ROWS;

            row.style.marginBottom = `${GAPS_BETWEEN_ROWS}px`;
          } else {
            rowsHeightPerPage += currentRowHeight;
          }
        } else {
          row.style.marginTop = `${pageHeigth - rowsHeightPerPage}px`;
          row.style.marginBottom = `${GAPS_BETWEEN_ROWS}px`;
          rowsHeightPerPage = currentRowHeight + GAPS_BETWEEN_ROWS;
        }
      }

      // end of block
      const pages: string[] = [];
      const pdfPageWidth = 210; // A4 width in mm
      const pdfPageHeight = 297; // A4 height in mm
      let currentPage = 0;
      let yPos = 0;
      const capturePage = async () => {
        const canvas = await html2canvas(container as HTMLElement, {
          scale: 2,
          y: currentPage * pageHeigth,
          height: pageHeigth,
        });
        return canvas.toDataURL("image/png");
      };

      while (yPos < container.offsetHeight) {
        const imageData = await capturePage();
        pages.push(imageData);
        currentPage++;
        yPos = currentPage * pageHeigth;
      }

      const doc = new jsPDF("p", "mm", "a4");
      pages.forEach((imageData, index) => {
        if (index > 0) {
          doc.addPage();
        }

        // Add padding around the content on each page
        const marginLeft = PAGE_MARGIN;
        const marginTop = PAGE_MARGIN;
        const imgWidth = pdfPageWidth - 2 * marginLeft;
        const imgHeight = pdfPageHeight - 2 * marginTop;

        doc.addImage(imageData, "PNG", marginLeft, marginTop, imgWidth, imgHeight);
      });
      doc.save(`${customNameToSave}.pdf`);
    }
  } catch (error) {
    console.error("Error generating PDF:", error);

    notifyIfErrorOccured();
  } finally {
    ReactDOM.unmountComponentAtNode(container);
    document.body.removeChild(container);
  }
};

export const exportSingleExpenseReportsWithDocs = async (
  contentToExport: React.JSX.Element,
  notifyIfErrorOccured: () => void,
  customNameToSavePDFFile: string,
  customNameToSavePDFZip: string,
  docFiles: { file: DocumentFileRef; customFileName: string }[]
) => {
  const pageHeigth = 1000;
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.transform = "translate(-9999px, -9999px)";
  container.style.width = "1400px";

  const contentWrappedByTheme = (
    <Provider store={setupStore()}>
      <ThemeProvider>
        <CssBaseline>{contentToExport}</CssBaseline>
      </ThemeProvider>
    </Provider>
  );

  try {
    // Render the JSX.Element into the container
    ReactDOM.render(contentWrappedByTheme, container);
    if (container) {
      document.body.appendChild(container);

      // block to add margins for rows
      let headerHeight = 0;
      const header = container.querySelectorAll(`div.${HEADER_FOR_PDF}`);
      for (let i = 0; i < header.length; i++) {
        const currHeader = header[i] as HTMLDivElement;
        headerHeight += currHeader.offsetHeight;
      }

      let rowsHeightPerPage = CONTENT_GAP + headerHeight;
      const rows = container.querySelectorAll(`div.${ROW_CLASSNAME}`);

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as HTMLDivElement;
        const currentRowHeight = row.offsetHeight;
        row.style.height = `${currentRowHeight}px`;

        if (rowsHeightPerPage + currentRowHeight <= pageHeigth) {
          if (rowsHeightPerPage + currentRowHeight + GAPS_BETWEEN_ROWS <= pageHeigth) {
            rowsHeightPerPage += currentRowHeight + GAPS_BETWEEN_ROWS;

            row.style.marginBottom = `${GAPS_BETWEEN_ROWS}px`;
          } else {
            rowsHeightPerPage += currentRowHeight;
          }
        } else {
          row.style.marginTop = `${pageHeigth - rowsHeightPerPage}px`;
          row.style.marginBottom = `${GAPS_BETWEEN_ROWS}px`;
          rowsHeightPerPage = currentRowHeight + GAPS_BETWEEN_ROWS;
        }
      }

      // end of block
      const pages: string[] = [];
      const pdfPageWidth = 297; // A4 width in mm
      const pdfPageHeight = 210; // A4 height in mm
      let currentPage = 0;
      let yPos = 0;
      const capturePage = async () => {
        const canvas = await html2canvas(container as HTMLElement, {
          scale: 2,
          y: currentPage * pageHeigth,
          height: pageHeigth,
        });
        return canvas.toDataURL("image/png");
      };

      while (yPos < container.offsetHeight) {
        const imageData = await capturePage();
        pages.push(imageData);
        currentPage++;
        yPos = currentPage * pageHeigth;
      }

      const doc = new jsPDF("l", "mm", "a4");
      pages.forEach((imageData, index) => {
        if (index > 0) {
          doc.addPage();
        }

        // Add padding around the content on each page
        const marginLeft = PAGE_MARGIN;
        const marginTop = PAGE_MARGIN;
        const imgWidth = pdfPageWidth - 2 * marginLeft;
        const imgHeight = pdfPageHeight - 2 * marginTop;

        doc.addImage(imageData, "PNG", marginLeft, marginTop, imgWidth, imgHeight);
      });

      // Create a zip file
      const zip = new JSZip();
      const pdfFolder = zip.folder(`${customNameToSavePDFZip}`);

      // Add generated PDF to the zip file
      const pdfBlob = doc.output("blob");
      pdfFolder && pdfFolder.file(`${customNameToSavePDFFile}.pdf`, pdfBlob);

      // Fetch and add each file to the zip
      const uniqueNames: Record<string, number> = {};

      for (let i = 0; i < docFiles.length; i++) {
        const fileData = await getDownloadedFile(docFiles[i].file);

        if (fileData?.data && pdfFolder) {
          const fileName = docFiles[i].customFileName;
          if (fileName in uniqueNames) {
            const fileExtenstion = getFileExtension(fileName);

            const nameWithoutExtention = fileName.replace(`.${fileExtenstion}`, "");

            const order = uniqueNames[fileName];

            pdfFolder.file(`${nameWithoutExtention}(${order + 1}).${fileExtenstion}`, fileData?.data);

            uniqueNames[fileName] = order + 1;
          } else {
            pdfFolder.file(`${fileName}`, fileData?.data);

            uniqueNames[fileName] = 0;
          }
        }

        fileData?.data && pdfFolder && pdfFolder.file(`${docFiles[i].customFileName}`, fileData?.data);
      }

      // Save the zip file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipUrl = URL.createObjectURL(zipBlob);

      const downloadLink = document.createElement("a");
      downloadLink.href = zipUrl;
      downloadLink.download = `${customNameToSavePDFZip}.zip`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  } catch (error) {
    console.error("Error generating PDF:", error);

    notifyIfErrorOccured();
  } finally {
    ReactDOM.unmountComponentAtNode(container);
    document.body.removeChild(container);
  }
};
